import React from 'react';
import styled, { css } from 'styled-components';
import { BreakpointsOfTOrT, breakpointMap, theme } from '../../../theme';
import { ItemCardBase } from './ItemCardBase';
import { ItemCardTimer } from './ItemCardTimer';
import { ItemCardLower } from './ItemCardLower';
import { ItemCardTitle } from './ItemCardTitle';
import { ItemCardPrice } from './ItemCardPrice';
import { ItemCardBidCount } from './ItemCardBidCount';
import { ItemCardUpper } from './ItemCardUpper';
import { ItemCardWrapper } from './ItemCardWrapper';
import { useBackgroundSize } from '../../../utils/useSetBackgroundSize';

type ContentCardSize = 'xxs' | 'xs' | 'sm' | 'lg' | 'full';

export type ItemCardProps = StyledItemCardProps & {
  imageUrl: string;
  price: string;
  title: string;
  icon: React.ReactNode;
  footer?: string;
  bidcount?: JSX.Element;
  timer?: string;
  className?: string;
};

export type StyledItemCardProps = {
  size: BreakpointsOfTOrT<ContentCardSize>;
};

const StyledItemCardWrapper = styled(ItemCardWrapper)`
  display: flex;
  justify-content: end;

  & *:not(:first-child) {
    padding-left: ${theme.spacings[1].rem};
  }
`;

export const ItemCardBackgroundImageContainer = styled.figure<{
  backgroundSize: string;
  imageUrl: string;
  cardSize: StyledItemCardProps['size'];
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background-image: ${({ imageUrl }) => `url('${imageUrl}')`};
  background-position: center;
  background-size: ${({ backgroundSize }) => backgroundSize};
  background-repeat: no-repeat;

  ${({ cardSize }) =>
    breakpointMap(cardSize, (size) =>
      size === 'xxs'
        ? css`
            height: 90px;
          `
        : size === 'xs'
        ? css`
            height: 120px;
          `
        : size === 'sm'
        ? css`
            height: 190px;
          `
        : css`
            height: 207px;
          `
    )};

  width: 100%;
  margin: 0;
  overflow: hidden;
  transform: scale(1);

  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: scale(1.01);
  }
`;

const ItemCardUnstyled: React.FC<ItemCardProps> = ({
  price,
  imageUrl,
  title,
  icon,
  bidcount,
  timer,
  size,
  className,
}) => {
  const getBackgroundSize = useBackgroundSize(imageUrl);

  return (
    <ItemCardBase size={size} className={className}>
      <ItemCardUpper>
        <ItemCardBackgroundImageContainer
          imageUrl={imageUrl}
          cardSize={size}
          backgroundSize={getBackgroundSize}
        />
      </ItemCardUpper>

      <ItemCardLower>
        <ItemCardTitle cardSize={size} size={6}>
          {title}
        </ItemCardTitle>
        <ItemCardWrapper>
          <ItemCardPrice cardSize={size} size={4}>
            {price}
          </ItemCardPrice>
          <ItemCardBidCount size="xs">{bidcount}</ItemCardBidCount>
        </ItemCardWrapper>
        <StyledItemCardWrapper>
          {icon && icon}
          {timer && <ItemCardTimer size="xs"> {timer}</ItemCardTimer>}
        </StyledItemCardWrapper>
      </ItemCardLower>
    </ItemCardBase>
  );
};

export const ItemCard = styled(ItemCardUnstyled)``;
